import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, ViewRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType } from "@ngx-formly/core";
import { Subject, takeUntil } from "rxjs";
import { AppStateService } from "src/app/core-module/services/app-state.service";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { ZtvStateService } from "../../../core-module/services/state/ztv-state.service";
import { SchemaDefaultComparatorService } from "../../../core-module/services/state/schema-default-comparator.service";
const customMinMaxValidator = (propsDetails, prop) => {
  return (control: FormControl) => {
    const propData = propsDetails[prop];
    const value = control.value;
    const min = propData.min;
    const max = propData.max;
    if (value < min || value > max) return { error: true };
    return null;
  };
};
const DIM_TO_OFF_LABEL = {
  "0.5V": "0.5V dim to off",
  "1.0V": "1.0V dim to off (for traditional dimmer)",
};

const data_types = {
  ZtvCurveSelection: "text",
  ZtvMinDimLevel: "number",
  ZtvEnabled: "boolean",
  ZtvDimToOffEnabled: "boolean",
  FadeOnTime: "number",
  FadeOffTime: "number",
  FastFadeEnabled: "boolean",
  DimToOffThreshold: "text",
  ZtvMinDimCurrent: "number",
  MinDimOption:'number',
  OverrideMode: "text",
};

@Component({
  standalone: false,
  selector: "app-ztv",
  templateUrl: "./ztv.component.html",
  styleUrls: ["./ztv.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZtvComponent extends FieldType implements OnInit, OnDestroy {
  @Input() field;
  propertiesDetails;
  propertiesInfo = {};
  ztvFormGroup = new FormGroup({});
  valueChangesSubs = {};
  quickConfigvalue: boolean;
  ztvdisbaled: LegacyAny;
  threshold: LegacyAny;
  overrideModeError = false;
  curveSelectionError = false;
private static readonly APR_CURVES = [
  '1-9V linear',
  '1-9V reversed linear',
  '0-5V reversed linear',
];
  private readonly destroy$ = new Subject<void>();
  constructor(
    private ref: ChangeDetectorRef, private appState: AppStateService,
    private aloAoc: AloAocStateService,
    private readonly ztv: ZtvStateService,
    private readonly schemaDefaults: SchemaDefaultComparatorService
  ) {
    super();
  }

  ngOnInit() {
    this.aloAoc.getquickConfig.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data === "quickConfig") {
        this.quickConfigvalue = true;
      } else if (data === "standard") {
        this.quickConfigvalue = false;
      }
      setTimeout(() => {
        if (this.ref && !(this.ref as ViewRef).destroyed) {
          this.ref.detectChanges();
        }
      });
    });

    this.ztv.getztvRestoreBtnClick.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res == "clicked") {
        this.resetDefault();
      }
    });

    if (this.field) {
      this.propertiesDetails = this.field.properties;
      this.buildInitialData();
      this.buildFormGroup();
      for (const key in this.ztvFormGroup.controls) {
        this.syncFromValueChanges(key);
      }
      this.saveValueOnChange();
    }
  }

  buildInitialData() {
    const localData = this.getDataFromLocal();
    if (
      !localData ||
      localData["fromDevice"] ||
      Object.keys(localData).length == 0
    ) {
      this.preparePropertiesInfo(localData);
    } else {
      // Validate that localData has the expected structure (info objects with 'value', 'error', etc.)
      // If LegacyAny property is a primitive value instead of an object, rebuild the structure
      const hasValidStructure = Object.keys(localData).every(key => {
        const prop = localData[key];
        return prop && typeof prop === 'object' && 'value' in prop;
      });

      if (hasValidStructure) {
        this.propertiesInfo = { ...localData };
        // null value means the error-state was cached; restore to schema default on reload
        const overrideInfo = this.propertiesInfo['OverrideMode'];
        if (overrideInfo && overrideInfo['value'] == null && overrideInfo['default'] != null) {
          overrideInfo['value'] = overrideInfo['default'];
          overrideInfo['enumItems']?.forEach((item) => {
            item['selected'] = item['value'] === overrideInfo['default'];
          });
        }
        this.validateCurveSelection();
      } else {
        // Data has raw values instead of info objects - need to rebuild structure
        this.preparePropertiesInfo(localData);
      }
    }
  }

  buildFormGroup() {
    for (const key in this.propertiesInfo) {
      const control = new FormControl(this.propertiesInfo[key]["value"], [
        customMinMaxValidator(this.propertiesInfo, key),
      ]);
      this.ztvFormGroup.addControl(key, control);
      if (
        this.propertiesInfo[key]["disabled"] ||
        !this.propertiesInfo[key]["useProp"]
      )
        this.disableControl(control, key);
      else this.updateOnlyValidity(control, key);
    }
  }

  syncFromValueChanges(key) {
    const control = this.ztvFormGroup.get(key);
    const propInfo = this.propertiesInfo[key];
    this.valueChangesSubs[key] = control.valueChanges.subscribe((res) => {
      propInfo["value"] = control.value;
      propInfo["error"] = control.invalid;
      propInfo["enumItems"].forEach(
        (item) => (item["selected"] = item.value == propInfo["value"])
      );
      if (key == "ZtvCurveSelection") {
        this.curveSelectionError = false;
  const mindimInfo = this.propertiesInfo["ZtvMinDimLevel"];
  const mindimControl = this.ztvFormGroup.get("ZtvMinDimLevel");
  if (mindimInfo && mindimControl) {
    if (this.isAprCurve(control.value)) {
      // APR curve: force min dim level to its default and grey it out
      mindimInfo["value"] = mindimInfo["default"];
      this.ztvFormGroup.patchValue(
        { ZtvMinDimLevel: mindimInfo["default"] },
        { emitEvent: false }
      );
      mindimInfo["disabled"] = true;
      this.disableControl(mindimControl, "ZtvMinDimLevel");
    } else {
      mindimInfo["disabled"] = this.checkForDefaultDisable("ZtvMinDimLevel");
      if (mindimInfo["disabled"]) {
        this.disableControl(mindimControl, "ZtvMinDimLevel");
      } else {
         this.enableControl(mindimControl, "ZtvMinDimLevel");
      }
    }
  }
}
this.saveValueOnChange();
    });
}
  onSliderValueChange(value, property) {
    const formvalue = {};
    formvalue[property] = value;
    this.ztvFormGroup.patchValue({
      ...formvalue,
    });
  }

  onCheckboxChange(prop, value, checked) {
    const propInfo = this.propertiesInfo[prop];
    const dimtooffdependentProps = [
      "DimToOffThreshold",
      "FadeOnTime",
      "FadeOffTime",
    ];
    const fastFadeDependProps = ["FadeOnTime", "FadeOffTime"];
    if (!propInfo) return;

    if (prop == "ZtvEnabled") {
      this.ztvFormGroup.patchValue(
        {
          ZtvEnabled: checked,
        },
        { emitEvent: false }
      );
      propInfo["value"] = checked;
      for (const key in this.propertiesInfo) {
        if (key != "ZtvEnabled") {
          const control = this.ztvFormGroup.get(key);
          const info = this.propertiesInfo[key];
          info["disabled"] = !checked || this.checkForDefaultDisable(key);
          if (dimtooffdependentProps.includes(key)) {
            const dimtooffcontrol = this.ztvFormGroup.get("ZtvDimToOffEnabled");
            if (dimtooffcontrol) {
              info["disabled"] = info["disabled"] || !dimtooffcontrol.value;
            }
          }
          if (fastFadeDependProps.includes(key)) {
            const fastfadecontrol = this.ztvFormGroup.get("FastFadeEnabled");
            if (fastfadecontrol) {
              info["disabled"] = info["disabled"] || fastfadecontrol.value;
            }
          }
          if (info["disabled"] || !info["useProp"]) {
            this.disableControl(control, key);
          } else {
            this.enableControl(control, key);
          }
        }
      }
    } else if (prop === "ZtvDimToOffEnabled" || prop === "FastFadeEnabled") {
      const valueObj = {};
      valueObj[prop] = checked;
      this.ztvFormGroup.patchValue(valueObj, { emitEvent: false });
      propInfo["value"] = checked;

      const fastfade = this.propertiesInfo["FastFadeEnabled"];
      const dimtooffEnable = this.propertiesInfo["ZtvDimToOffEnabled"];
      dimtooffdependentProps.forEach((dimtooffdependentprop) => {
        const info = this.propertiesInfo[dimtooffdependentprop];
        if (info) {
          const control = this.ztvFormGroup.get(dimtooffdependentprop);
          if (dimtooffEnable) info["disabled"] = !dimtooffEnable.value;
          if (fastFadeDependProps.includes(dimtooffdependentprop) && fastfade) {
            info["disabled"] = info["disabled"] || fastfade.value;
          }
          if (info["disabled"] || !info["useProp"]) {
            this.disableControl(control, dimtooffdependentprop);
          } else {
            this.enableControl(control, dimtooffdependentprop);
          }
        }
      });
    } else if (prop === "DimToOffThreshold") {
      const enumItems = propInfo["enumItems"];
      enumItems.forEach((item) => {
        item["selected"] =
          (item["value"] == value && checked) ||
          (item["value"] !== value && !checked)
            ? true
            : false;
        if (item["selected"]) {
          propInfo["value"] = item["value"];
        }
      });
    } else if (prop === "OverrideMode") {
      this.onOverrideModeChange(propInfo, value, checked);
    } else {
      if (propInfo["hasOneOf"]) {
        propInfo["disablepropOnUse"].forEach((dependProp) => {
          const depcontrol = this.ztvFormGroup.get(dependProp);
          const depPropInfo = this.propertiesInfo[dependProp];
          if (depcontrol) {
            depPropInfo["useProp"] = !checked;
            if (checked) this.disableControl(depcontrol, dependProp);
            else this.enableControl(depcontrol, dependProp);
          }
        });
        const control = this.ztvFormGroup.get(prop);
        propInfo["useProp"] = checked;
        if (control) {
          if (!checked) this.disableControl(control, prop);
          else this.enableControl(control, prop);
        }
        if(prop === 'ZtvMinDimLevel' || prop === 'ZtvMinDimCurrent'){
          const mindimoption = this.propertiesInfo['MinDimOption'];
          if(mindimoption) mindimoption['value'] = (prop == 'ZtvMinDimLevel' && checked) ||  (prop == 'ZtvMinDimCurrent' && !checked)? 0 : 1;
        }
      }
    }
    this.saveValueOnChange();
  }

  private onOverrideModeChange(propInfo, value, checked) {
    const overrideItems = propInfo["enumItems"];
    if (checked) {
      overrideItems.forEach((item) => {
        item["selected"] = item["value"] === value;
      });
      propInfo["value"] = value;
    } else {
      const currentIndex = overrideItems.findIndex(item => item["value"] === value);
      const nextIndex = (currentIndex + 1) % overrideItems.length;
      overrideItems.forEach((item, i) => {
        item["selected"] = i === nextIndex;
      });
      propInfo["value"] = overrideItems[nextIndex]["value"];
    }
    this.overrideModeError = false;
  }

  disableControl(control: AbstractControl, controlname) {
    this.updateOnlyValidity(control, controlname);
    control.disable({ emitEvent: false });
  }

  enableControl(control: AbstractControl, controlname) {
    control.enable({ emitEvent: false });
    this.updateOnlyValidity(control, controlname);
  }

  updateOnlyValidity(control: AbstractControl, controlName) {
    const info = this.propertiesInfo[controlName];

    // Safety check: ensure info is an object with expected structure
    if (!info || typeof info !== 'object' || !('value' in info)) {
      return;
    }

    const isdisabled = control.disabled;
    if (isdisabled) control.enable({ emitEvent: false });
    if (this.valueChangesSubs[controlName]) {
      this.valueChangesSubs[controlName].unsubscribe();
      delete this.valueChangesSubs[controlName];
      control.updateValueAndValidity();
      this.syncFromValueChanges(controlName);
    } else {
      control.updateValueAndValidity();
    }
    info.error = control.invalid;
    if (isdisabled) control.disable({ emitEvent: false });
  }

  preparePropertiesInfo(propOptions = null) {
    const propOptionKeys = propOptions ? Object.keys(propOptions) : [];
    for (const key in this.propertiesDetails) {
      if (
        this.isPropertyAvailableInOneOf(key) ||
        this.isPropertyAvailableInRequriredProps(key) || key == 'MinDimOption'
      ) {
        const propDetails = this.propertiesDetails[key];
        let info = {};
        info["default"] = this.getDefault(propDetails, key);
        info["min"] = propDetails["minimum"];
        info["max"] = propDetails["maximum"];
        info["sliderMax"] = propDetails["maximum"];
        info["sliderMin"] = propDetails["minimum"];
        info["options"] = {
          floor: propDetails["minimum"],
          ceil: propDetails["maximum"],
          minLimit: propDetails["minimum"],
          maxLimit: propDetails["maximum"],
        };
        info["value"] = info["default"];
        if (propOptions && propOptionKeys.includes(key) || key == 'MinDimOption') {
          info = {
            ...info,
            value: key == 'MinDimOption' ? this.getInitialMindimOption(propOptions, propOptionKeys) : propOptions[key],
          };
        }

        this.propertiesInfo[key] = info;
      }
    }
    for (const key in this.propertiesDetails) {
      if (
        this.isPropertyAvailableInOneOf(key) ||
        this.isPropertyAvailableInRequriredProps(key) || key == 'MinDimOption'
      ) {
        const propDetails = this.propertiesDetails[key];
        const info = this.propertiesInfo[key];
        info["disabled"] = this.checkForDefaultDisable(key);
        info["showPropOnUi"] = this.checkForDefaultShow(key);
        info["error"] = false;
        info["hasOneOf"] = this.isPropertyAvailableInOneOf(key);
        info["disablepropOnUse"] = this.disableOneOfOnUse(key);
        info["useProp"] = this.canUseProp(
          key,
          info["hasOneOf"],
          info["disablepropOnUse"]
        );
        info["enumItems"] = this.prepareEnumItems(
          propDetails,
          key,
          info["value"]
        );
      }
    }
    // Detect missing or out-of-enum OverrideMode from device read (e.g. code 1303).
    if (propOptions !== null && this.isPropertyAvailableInRequriredProps('OverrideMode')) {
      const deviceValue = propOptionKeys.includes('OverrideMode') ? propOptions['OverrideMode'] : undefined;
      const validValues: string[] = this.propertiesDetails?.['OverrideMode']?.['enum'] || [];
      if (deviceValue === undefined || !validValues.includes(deviceValue)) {
        this.overrideModeError = true;
        if (this.propertiesInfo['OverrideMode']?.enumItems) {
          this.propertiesInfo['OverrideMode'].enumItems.forEach((item) => {
            item['selected'] = false;
          });
          this.propertiesInfo['OverrideMode'].value = null;
        }
      }
    }
    
    if (propOptions !== null && (this.isPropertyAvailableInOneOf('ZtvCurveSelection') || this.isPropertyAvailableInRequriredProps('ZtvCurveSelection'))) {
      const deviceValue = propOptionKeys.includes('ZtvCurveSelection') ? propOptions['ZtvCurveSelection'] : undefined;
      if (deviceValue !== undefined) {
        const propEnum = this.propertiesDetails?.['ZtvCurveSelection']?.['enum'] || [];
        const found = propEnum.includes(deviceValue) || 
                      propEnum.includes(parseInt(deviceValue)) || 
                      propEnum.includes(String(deviceValue));
        if (!found) {
          this.curveSelectionError = true;
        }
      }
    }
    if (this.curveSelectionError || this.overrideModeError) {
      if (this.ref && !(this.ref as ViewRef).destroyed) {
        this.ref.markForCheck();
      }
      this.ztv.ztvInputError(this.isFormHasError());
    }
  }

  validateCurveSelection() {
    this.curveSelectionError = false;
    if ((this.isPropertyAvailableInOneOf('ZtvCurveSelection') || this.isPropertyAvailableInRequriredProps('ZtvCurveSelection')) 
        && this.propertiesInfo['ZtvCurveSelection']) {
      const currentValue = this.propertiesInfo['ZtvCurveSelection'].value;
      if (currentValue !== undefined && currentValue !== null) {
        const propEnum = this.propertiesDetails?.['ZtvCurveSelection']?.['enum'] || [];
        if (propEnum.length > 0) {
          const found = propEnum.includes(currentValue) || 
                        propEnum.includes(parseInt(currentValue)) || 
                        propEnum.includes(String(currentValue));
          if (!found) {
            this.curveSelectionError = true;
            if (this.ref && !(this.ref as ViewRef).destroyed) {
              this.ref.markForCheck();
            }
            this.ztv.ztvInputError(true);
          }
        }
      }
    }
  }

  getInitialMindimOption(data, keys){
    if(keys.includes("MinDimOption")) return data['MinDimOption'];
    if(keys.includes('ZtvMinDimLevel')) return 0;
    if(keys.includes('ZtvMinDimCurrent')) return 1;
    return 0;
  }
  private isAprCurve(curve): boolean {
    return ZtvComponent.APR_CURVES.includes(curve);
  }
  checkForDefaultShow(prop){
    return true;
  }

  checkForDefaultDisable(prop) {
    if (
  prop === "ZtvMinDimLevel" &&
  (this.isPropertyAvailableInOneOf("ZtvCurveSelection") ||
    this.isPropertyAvailableInRequriredProps("ZtvCurveSelection")) &&
  this.isAprCurve(this.propertiesInfo["ZtvCurveSelection"]?.value)
) {
  return true;
}
    if (
      prop !== "ZtvEnabled" &&
      (this.isPropertyAvailableInOneOf("ZtvEnabled") ||
        this.isPropertyAvailableInRequriredProps("ZtvEnabled"))
    ) {
      const ztvenabledValue = this.propertiesInfo['ZtvEnabled'].value;
      if (!ztvenabledValue) return true;
    }
    const dimtooffdependentProps = [
      "DimToOffThreshold",
      "FadeOnTime",
      "FadeOffTime",
    ];
    const fastFadeDependProps = ["FadeOnTime", "FadeOffTime"];
    if (
      dimtooffdependentProps.includes(prop) &&
      (this.isPropertyAvailableInOneOf("ZtvDimToOffEnabled") ||
        this.isPropertyAvailableInRequriredProps("ZtvDimToOffEnabled"))
    ) {
      const dimtooffValue = this.propertiesInfo['ZtvDimToOffEnabled'].value;
      if (!dimtooffValue) return true;
    }
    if (
      fastFadeDependProps.includes(prop) &&
      (this.isPropertyAvailableInOneOf("FastFadeEnabled") ||
        this.isPropertyAvailableInRequriredProps("FastFadeEnabled"))
    ) {
      const fastFadeValue = this.propertiesInfo['FastFadeEnabled'].value;
      if (fastFadeValue) return true;
    }
    return false;
  }

  getDefault(propDetails, prop?) {
    const propdetailsKeys = Object.keys(propDetails);
    let defaultValue = null;
    if (propdetailsKeys.includes("default")) defaultValue = propDetails["default"];
    if (propdetailsKeys.includes("propertyDefault"))
      defaultValue = propDetails["propertyDefault"];
    if (propdetailsKeys.includes("const")){
      defaultValue = propDetails["const"];
    }
    if(prop == 'ZtvCurveSelection' && !isNaN(defaultValue)){
      const propEnum = propDetails.enum;
      for(let i = 0; i<propEnum.length; i++){
        if(propEnum[i] == defaultValue) {
          defaultValue = propEnum[i+1];
          break;
        }
      }
    }
    return defaultValue;
  }

  prepareEnumItems(propDetails, prop, defaultValue) {
    const items = [];
    if (prop == "ZtvCurveSelection") {
      for (let i = 0; i < propDetails["enum"].length; i++) {
        if(isNaN(propDetails["enum"][i])){
          items.push({
            label: propDetails["enum"][i],
            value: propDetails["enum"][i],
            selected: propDetails["enum"][i] == defaultValue,
          });
        }
      }
    }
    if (prop == "FadeOffTime" || prop == "FadeOnTime") {
      const min = propDetails["minimum"];
      const max = propDetails["maximum"];
      const multiplier = propDetails["multipleOf"];
      for (let i = min; i <= max; i += multiplier) {
        items.push({
          label: i,
          value: i,
          selected: i == defaultValue,
        });
      }
    }
    if (prop == "DimToOffThreshold") {
      propDetails["enum"].forEach((val) => {
        items.push({
          value: val,
          label: DIM_TO_OFF_LABEL[val],
          selected: val == defaultValue,
        });
      });
    }
    if (prop == "OverrideMode") {
      propDetails["enum"].forEach((val) => {
        items.push({
          value: val,
          label: val,
          selected: val == defaultValue,
        });
      });
    }
    return items;
  }

  canUseProp(prop, isoneof, disableProps) {
    if (isoneof) {
      if(prop == 'ZtvMinDimLevel' || prop == 'ZtvMinDimCurrent'){
        const mindimOption = this.propertiesInfo['MinDimOption'].value;
        return (prop == 'ZtvMinDimLevel' && mindimOption == 0) || (prop == 'ZtvMinDimCurrent' && mindimOption == 1);
      }
    }
    return true;
  }

  isPropertyAvailableInRequriredProps(prop) {
    const reqProps = this.field.requiredProperties;
    return reqProps.includes(prop);
  }

  isPropertyAvailableInOneOf(prop) {
    const oneOf = this.field.oneOf;
    if (oneOf) {
      return !!oneOf.find((ele) => ele["dependencies"][prop]);
    }
    return false;
  }

  disableOneOfOnUse(prop) {
    if (this.isPropertyAvailableInOneOf(prop)) {
      const oneOf = this.field.oneOf;
      const dependencies = oneOf.find((ele) => ele["dependencies"][prop]);
      if (dependencies) return dependencies["dependencies"][prop];
    }
    return [];
  }

  getDataFromLocal() {
    // Use centralized method to get feature data
    // This automatically prioritizes SaveSetData over ConfigurationData

    // Try ztvObj first (legacy format)
    let ztvData = this.appState.getFeatureData('ztvObj');
    if (ztvData && Object.keys(ztvData).length > 0) {
      return ztvData;
    }

    // Try ZTV format
    ztvData = this.appState.getFeatureData('ZTV');
    if (ztvData) {
      // Check if this is from SaveSetData (user-configured) or ConfigurationData (device defaults)
      const saveData = this.appState.getSaveSetData();
      const isFromSaveData = saveData?.resp?.ZTV != null;

      // Only set fromDevice flag if NOT from SaveSetData
      if (!isFromSaveData) {
        ztvData["fromDevice"] = true;
      }
      return ztvData;
    }

    return null;
  }

  setDataToLocal() {
    // Use centralized method to save feature data
    // This automatically saves to both ConfigurationData and SaveSetData
    this.appState.setFeatureData('ztvObj', { ...this.propertiesInfo });
  }

  checkDefault() {
    for (const key in this.propertiesInfo) {
      const info = this.propertiesInfo[key];
      const schemaField = this.propertiesDetails?.[key] || { default: info["default"] };
      if (!this.schemaDefaults.isValueAtSchemaDefault(info["value"], schemaField)) {
        return false;
      }
      if (
        info["useProp"] !=
        this.canUseProp(key, info["hasOneOf"], info["disablepropOnUse"])
      )
        return false;
    }
    return true;
  }

   resetDefault() {
    const formData = {};
    for (const key in this.propertiesInfo) {
      const info = this.propertiesInfo[key];
      info["value"] = info["default"];
    }
    for (const key in this.propertiesInfo) {
      const info = this.propertiesInfo[key];
      info["useProp"] = this.canUseProp(
        key,
        info["hasOneOf"],
        info["disablepropOnUse"]
      );
      info["disabled"] = this.checkForDefaultDisable(key);
      info["error"] = false;
      info["showPropOnUi"] = this.checkForDefaultShow(key);
      if (info["enumItems"]) {
        info["enumItems"].forEach((item) => {
          item["selected"] = item["value"] == info["default"];
        });
      }
      const control = this.ztvFormGroup.get(key);
      if (control) {
        formData[key] = info["value"];
        if (info["disabled"] || !info["useProp"])
          this.disableControl(control, key);
        else this.enableControl(control, key);
      }
    }
    this.ztvFormGroup.patchValue(
      {
        ...formData,
      },
      { emitEvent: false }
    );
    this.clearAllFormValidationErrors();
    this.overrideModeError = false;
    this.curveSelectionError = false;
    this.saveValueOnChange();
    this.ref.markForCheck();
  }

  private clearAllFormValidationErrors() {
    for (const key in this.propertiesInfo) {
      const control = this.ztvFormGroup.get(key);
      const info = this.propertiesInfo[key];
      if (control) {
        control.setErrors(null);
        control.markAsPristine();
        control.markAsUntouched();
        control.updateValueAndValidity({ emitEvent: false });
        info["error"] = false;
      }
    }
  }

  isFormHasError() {
    if (this.overrideModeError || this.curveSelectionError) {
      return true;
    }
    for (const key in this.propertiesInfo) {
      const info = this.propertiesInfo[key];
      if (info["error"]) {
        return true;
      }
    }
    return false;
  }

  saveValueOnChange() {
    this.setDataToLocal();
    this.ztv.ztvEditedVal(this.checkDefault());
    this.sendZTVData();
    this.ztv.ztvInputError(this.isFormHasError());
  }

  sendZTVData() {
    const data = {};
    for (const key in this.propertiesInfo) {
      const info = this.propertiesInfo[key];
      if (info["useProp"]) {
        if (data_types[key] == "number") data[key] = Number(info["value"]);
        else data[key] = info["value"];
      }
    }
    this.ztv.sendZtvObj(data);
  }

  featureHasProperty(prop) {
    return !!(this.propertiesInfo[prop] && this.propertiesInfo[prop]["showPropOnUi"]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    for (const key in this.valueChangesSubs) {
      if (this.valueChangesSubs[key]) {
        this.valueChangesSubs[key].unsubscribe();
      }
    }
    this.valueChangesSubs = {};
  }
}

